import { useState } from "react";
import type { Annotation3D } from "./annotation3DTypes";

interface Annotation3DOverlayProps {
  annotations: Annotation3D[];
  onResolve: (id: string) => void;
  onDelete: (id: string) => void;
}

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

/**
 * Renders annotation pins as an HTML overlay positioned by the Viewport.
 * The Viewport sets CSS transform on each pin's container div based on
 * the projected 3D position.
 */
export default function Annotation3DOverlay({ annotations, onResolve, onDelete }: Annotation3DOverlayProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <div className="annotation-overlay">
      {annotations.map((ann) => (
        <div
          key={ann.id}
          className="annotation-pin-container"
          data-annotation-id={ann.id}
          data-position={JSON.stringify(ann.position)}
        >
          <button
            className={`annotation-pin ${ann.resolved ? "annotation-pin--resolved" : ""}`}
            onClick={() => setExpandedId(expandedId === ann.id ? null : ann.id)}
            title={ann.text}
          >
            {ann.kind === "issue" ? "!" : ann.kind === "approval" ? "✓" : "💬"}
          </button>
          {expandedId === ann.id && (
            <div className="annotation-bubble">
              <div className="annotation-bubble-header">
                <span className="annotation-author">{ann.author}</span>
                <span className="annotation-time">{timeAgo(ann.createdAt)}</span>
              </div>
              <div className="annotation-text">{ann.text}</div>
              <div className="annotation-actions">
                {!ann.resolved && (
                  <button className="annotation-action-btn annotation-action-btn--resolve" onClick={() => onResolve(ann.id)}>
                    Resolve
                  </button>
                )}
                <button className="annotation-action-btn annotation-action-btn--delete" onClick={() => { onDelete(ann.id); setExpandedId(null); }}>
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
